/** Typed fetch for the Worker API. Errors carry a translated message ready to show. */
import type { ApiErrorBody } from "../../contract";
import { getLanguage, t } from "./i18n";
import { keepsSnapshot, noteNetworkFailure, noteNetworkSuccess, noteSnapshotUsed, readSnapshot, writeSnapshot } from "./offline";

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string, public issues: Array<{ path: string; message: string }> = []) {
    super(message);
  }
}

/** True when the request never reached Hondo (no signal, airplane mode, server unreachable). */
export const isOffline = (err: unknown): boolean => err instanceof ApiError && err.status === 0;

let onUnauthorized: () => void = () => {};
export const setUnauthorizedHandler = (fn: () => void) => { onUnauthorized = fn; };

export async function request<T>(method: string, path: string, body?: unknown, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("accept-language", getLanguage());
  let payload: BodyInit | undefined;
  if (body instanceof Blob || body instanceof ArrayBuffer) payload = body as BodyInit;
  else if (body !== undefined) { headers.set("content-type", "application/json"); payload = JSON.stringify(body); }
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { ...init, method, headers, body: payload, credentials: "same-origin" });
  } catch {
    noteNetworkFailure();
    // Reading without signal: show the last saved copy of field screens, clearly marked.
    if (method === "GET" && keepsSnapshot(path)) {
      const saved = await readSnapshot(path);
      if (saved) { noteSnapshotUsed(saved.saved_at); return saved.data as T; }
    }
    throw new ApiError(0, "offline", t("You're offline. We'll keep trying."));
  }
  noteNetworkSuccess();
  if (res.status === 401) onUnauthorized();
  const text = await res.text();
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = null; }
  if (!res.ok) {
    const err = (data ?? {}) as ApiErrorBody;
    throw new ApiError(res.status, err.error ?? "error", t(err.message ?? "Something went wrong on our side. Try again."), err.issues ?? []);
  }
  if (method === "GET" && keepsSnapshot(path)) void writeSnapshot(path, data);
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>("GET", path),
  post: <T>(path: string, body?: unknown) => request<T>("POST", path, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  put: <T>(path: string, body: unknown, headers?: HeadersInit) => request<T>("PUT", path, body, { headers }),
  del: <T>(path: string) => request<T>("DELETE", path),
};

/** Build a query string, skipping empty values. */
export function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== "") p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : "";
}
